import { useState, useEffect } from 'react';

export const THEME_STORAGE_KEY = 'dwellia_theme';
export const THEME_CHANGE_EVENT = 'dwellia-theme-change';

function getSafeLocalStorage() {
  if (typeof window === 'undefined') return null;

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function normalizeRawThemeStorageToken(value) {
  if (typeof value !== 'string') return null;
  return value.trim().replace(/^['"]+|['"]+$/g, '').toLowerCase();
}

export function normalizeThemeStorageValue(value) {
  const normalized = normalizeRawThemeStorageToken(value);
  if (normalized === 'dark' || normalized === 'light') return normalized;

  return null;
}

function readStoredTheme() {
  const storage = getSafeLocalStorage();
  if (!storage) return null;

  try {
    return normalizeThemeStorageValue(storage.getItem(THEME_STORAGE_KEY));
  } catch {
    return null;
  }
}

function persistStoredTheme(isDark) {
  const storage = getSafeLocalStorage();
  if (!storage) return;

  try {
    storage.setItem(THEME_STORAGE_KEY, isDark ? 'dark' : 'light');
  } catch {
    // Ignore storage write failures (e.g., privacy mode restrictions).
  }
}

export function createThemeChangeEvent(isDark) {
  const fallbackPayload = { type: THEME_CHANGE_EVENT, detail: { isDark } };

  if (typeof window === 'undefined') {
    return fallbackPayload;
  }

  if (typeof window.CustomEvent === 'function') {
    try {
      return new window.CustomEvent(THEME_CHANGE_EVENT, { detail: { isDark } });
    } catch {
      // Fall through to Event/plain fallback when CustomEvent construction is unavailable/restricted.
    }
  }

  if (typeof window.Event === 'function') {
    try {
      const event = new window.Event(THEME_CHANGE_EVENT);
      event.detail = { isDark };
      return event;
    } catch {
      // Fall back to a plain payload when Event construction is unavailable/restricted.
    }
  }

  return fallbackPayload;
}

/**
 * Manages light/dark theme. Light is the default.
 * Persists preference to localStorage and applies/removes the 'dark' class on <html>.
 */
function dispatchThemeChangeEvent(isDark) {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
    return;
  }

  const event = createThemeChangeEvent(isDark);
  const canValidateEventInstance = typeof window.Event === 'function';

  if (
    !event ||
    typeof event.type !== 'string' ||
    typeof event.preventDefault !== 'function'
  ) {
    return;
  }

  if (canValidateEventInstance && !(event instanceof window.Event)) {
    return;
  }

  try {
    window.dispatchEvent(event);
  } catch {
    // Ignore dispatch failures in restricted runtimes.
  }
}

export function useTheme() {
  const [isDark, setIsDark] = useState(() => {
    const stored = readStoredTheme();
    if (stored !== null) return stored === 'dark';
    // Default: light mode
    return false;
  });

  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    const html = document.documentElement;
    if (isDark) {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }

    persistStoredTheme(isDark);
    dispatchThemeChangeEvent(isDark);
  }, [isDark]);

  const toggle = () => setIsDark((d) => !d);

  return { isDark, toggle };
}
